import React, { useState, useEffect } from 'react';
import { api } from '@services/api';
import {
  Container,
  TextField,
  Typography,
  Grid,
  FormControl
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { type Color } from '@material-ui/lab/Alert';
import { useFiles } from '@base/context/files';
import { Upload } from '@components/Upload';
import { FileList } from '@components/FileList';
import { z } from 'zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DivButtons,
  DivExames,
  DivGridFieldDataContract,
  PaperContainer
} from './styles';
import { Button } from '@components/Button';
import { Plus } from '@phosphor-icons/react';
import { useAuth } from '@context/auth';
import { Exames } from '@components/Exames';
import { Servicos } from '@components/Servicos';
import { Toxicologico } from '@components/Toxicologico';
import { useQuery } from '@tanstack/react-query';
import { ToastMessage } from '@components/ToastMessage';
import { AppError } from '@utils/AppError';
import { NavLink } from '@components/NavLink';

interface IRiscos {
  id_risco: string;
  nome: string;
}

const contractSchema = z.object({
  nome_contrato: z.string().trim().optional().default(''),
  cod_contrato: z
    .string()
    .trim()
    .min(1, { message: 'O campo não pode estar vazio.' }),
  data_criacao: z.string().optional(),
  data_renovacao: z.string().optional(),
  arquivo: z.string().array().optional(),
  usuario: z.string(),
  exame: z.array(
    z.object({
      id_exames: z.string().or(z.number()).optional(),
      nome: z.string(),
      valor: z.number().or(z.string())
    })
  ),
  risco: z
    .array(
      z.object({
        id_risco: z.string().or(z.number()).optional(),
        nome: z.string()
      })
    )
    .optional(),
  servico: z.array(
    z.object({
      id_servicos: z.string().or(z.number()).optional(),
      nome: z.string(),
      valor: z.number().or(z.string())
    })
  ),
  exame_toxi: z.array(
    z.object({
      id_exame_tox: z.string().or(z.number()).optional(),
      nome: z.string(),
      valor: z.number().or(z.string())
    })
  )
});

type ContractSaveFormData = z.infer<typeof contractSchema>;

export default function CadastroContratoCredenciado() {
  const [risco, setRisco] = useState<IRiscos[]>([]);

  const [dialogSevices, setDialogServices] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    cod: '',
    message: '',
    color: '' as Color,
    show: false
  });

  const [dialog, setDialog] = useState<boolean>(false);
  const [dialogToxico, setDialogToxico] = useState<boolean>(false);

  const { user } = useAuth();
  const hora = new Date();
  const newHora = hora.toLocaleString('pt-BR', { hour12: false });
  const userUpdate = `${user.nome} ${newHora}`;

  const {
    uploadedFiles: files,
    fetchDataFiles,
    receivedCodContrato
  } = useFiles();

  const filesNames = files.map((file) => {
    return file.name;
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    control,
    getValues,
    setValue
  } = useForm<ContractSaveFormData>({
    resolver: zodResolver(contractSchema),
    defaultValues: {
      usuario: userUpdate
    }
  });

  const { append, remove } = useFieldArray({
    control,
    name: 'risco'
  });

  const { data, isSuccess } = useQuery({
    queryKey: ['comboRiscos'],
    queryFn: async () => {
      const res = await api.get(`/ravim/riscos`);
      return res.data;
    }
  });

  useEffect(() => {
    receivedCodContrato(getValues('cod_contrato'));
  }, [getValues('cod_contrato')]);

  function handleAddRiscos(riscos: IRiscos[]) {
    setRisco(riscos);
  }

  async function handleSubmitForm(data: ContractSaveFormData) {
    try {
      await api.post('ravim/cad_cred', data);

      setToastMessage({
        cod: '200',
        color: 'success',
        message: 'Salvo com sucesso',
        show: true
      });

      setTimeout(() => {
        window.location.replace('/contratos-credenciado');
      }, 2000);
    } catch (error) {
      if (error instanceof AppError) {
        setToastMessage({
          cod: '',
          color: 'error',
          message: error.message,
          show: true
        });
        throw error.message;
      }
    }
  }

  useEffect(() => {
    if (filesNames) {
      setValue('arquivo', filesNames);
    }
  }, [files]);

  useEffect(() => {
    fetchDataFiles([]);
  }, []);

  return (
    <Container>
      <Typography variant="h4" align="center">
        Cadastro de Contrato de Credenciados
      </Typography>

      <form onSubmit={handleSubmit(handleSubmitForm)}>
        <PaperContainer>
          <Typography variant="h5">Dados do Cadastro</Typography>
          <Grid container spacing={1}>
            <Grid item xs>
              <DivGridFieldDataContract>
                <TextField
                  variant="outlined"
                  label="Nome do contrato"
                  {...register('nome_contrato')}
                />
                <TextField
                  variant="outlined"
                  label="Código do Contrato"
                  error={!!errors.cod_contrato?.message}
                  helperText={errors.cod_contrato?.message}
                  {...register('cod_contrato')}
                />
                <TextField
                  id="date"
                  label="Data de Criação"
                  type="date"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }}
                  {...register('data_criacao')}
                />
                <TextField
                  id="date"
                  label="Data de Renovação"
                  type="date"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }}
                  {...register('data_renovacao')}
                />
              </DivGridFieldDataContract>
              <Upload />
              <FileList />
            </Grid>
          </Grid>

          <Typography variant="h5">Exames</Typography>
          <DivExames>
            <Button
              title="Adicionar Exames"
              onClick={() => {
                setDialog(!dialog);
              }}
              icon={<Plus size={20} />}
              type="button"
            />
            <Button
              title="Exames Toxicólogico"
              onClick={() => {
                setDialogToxico(!dialogToxico);
              }}
              icon={<Plus size={20} />}
              type="button"
            />
            <Button
              icon={<Plus size={20} />}
              title="Adicionar Serviços"
              onClick={() => {
                setDialogServices(!dialogSevices);
              }}
              type="button"
            />
          </DivExames>
          <FormControl>
            <div>
              <Autocomplete
                multiple
                id="tags-outlined"
                options={isSuccess ? data : []}
                getOptionLabel={(option: IRiscos) => {
                  return `${option.nome}`
                    ? `${option.id_risco} - ${option.nome}`
                    : '';
                }}
                onChange={(e: any, newValue, reason, detail) => {
                  handleAddRiscos(newValue);

                  if (reason === 'remove-option' || reason === 'clear') {
                    if (detail) {
                      const index = risco.findIndex(
                        (i) => i === detail?.option
                      );
                      remove(index);
                    }
                  } else if (reason === 'select-option') {
                    if (detail?.option) {
                      append({ ...detail?.option });
                    }
                  }
                  // console.log(reason, detail)
                }}
                value={risco}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Riscos"
                    placeholder="Riscos"
                  />
                )}
              />
            </div>
            <Exames
              control={control}
              openDialog={dialog}
              closeDialog={() => {
                setDialog(!dialog);
              }}
            />
            <Servicos
              control={control}
              openModal={dialogSevices}
              closeModal={() => {
                setDialogServices(false);
              }}
            />

            <Toxicologico
              control={control}
              openModal={dialogToxico}
              closeModal={() => {
                setDialogToxico(false);
              }}
            />
          </FormControl>
        </PaperContainer>
        <DivButtons>
          <NavLink to="/contratos-credenciado">
            <Button title="Sair" variantstyle="RED" type="button" />
          </NavLink>
          <Button type="submit" title="Salvar" isLoading={isSubmitting} />
        </DivButtons>
      </form>

      <ToastMessage
        code={toastMessage.cod}
        message={toastMessage.message}
        show={toastMessage.show}
        severity={toastMessage.color}
        onClose={() => {
          setToastMessage({
            cod: toastMessage.cod,
            color: toastMessage.color,
            message: toastMessage.message,
            show: toastMessage.show
          });
        }}
      />
    </Container>
  );
}
